'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  CreditCard,
  Download,
  Flame,
  Grid3X3,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Textarea } from '@/shared/components/ui/textarea';
import { useAppContext } from '@/shared/contexts/app';
import {
  trackPhotoEffects,
  trackDailyCheckin,
  trackImageToImagePageView,
  trackEffectSelection,
  trackImageUpload,
  trackGenerateClick,
  trackCheckinDialogOpen,
} from '@/shared/lib/analytics';
import { cn } from '@/shared/lib/utils';

export interface ImageToImageEffect {
  id: string;
  name: string;
  emoji: string;
  buttonText: string;
  description: string; // 效果描述
  subtitle: string; // 副标题
  preview: string;
  model: string;
  provider: string;
  prompt?: string;
  isCustom?: boolean; // 标记是否为自定义选项
}

interface ImageToImageGeneratorProps {
  defaultEffect?: string;
  effects?: ImageToImageEffect[];
  allowMultipleImages?: boolean;
  maxImages?: number;
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  effect?: string;
  prompt?: string;
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

interface CheckinStatus {
  hasCheckedInToday: boolean;
  consecutiveDays: number;
  tomorrowRewardCredits: number;
}

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;

// Default model configuration
const DEFAULT_MODEL = 'google/nano-banana-pro';
const DEFAULT_PROVIDER = 'replicate';

// Image-to-Image effects configuration
const DEFAULT_IMAGE_TO_IMAGE_EFFECTS: ImageToImageEffect[] = [
  {
    id: 'cinematic',
    name: 'Cinematic Enhancement',
    emoji: '🎬',
    buttonText: 'Generate Cinematic Version',
    description: 'Turn ordinary photos into cinematic-quality visuals',
    subtitle: 'Enhances lighting, contrast, and mood without changing composition',
    preview: 'https://r2.zestygen.com/effects/10.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform this image into a cinematic masterpiece with professional color grading, dramatic lighting, and film-like quality. Apply Hollywood-style cinematography with enhanced contrast, rich shadows, warm highlights, and a widescreen aesthetic. Add depth of field, lens flares, and atmospheric effects to create a movie-quality visual experience. Enhance the mood and emotion while maintaining the original composition and subjects.',
  },
  {
    id: 'detail-boost',
    name: 'Detail Boost',
    emoji: '✨',
    buttonText: 'Enhance Details',
    description: 'Enhance clarity, textures, and fine details',
    subtitle: 'Perfect for portraits and product images',
    preview: 'https://r2.zestygen.com/effects/20.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Enhance and amplify all details in this image to maximum clarity and sharpness. Increase texture definition, fine details, and micro-contrasts. Sharpen edges, enhance surface textures, bring out intricate patterns, and reveal hidden details. Apply advanced detail enhancement while maintaining natural appearance and avoiding over-processing. Focus on clarity, definition, and visual precision across all elements.',
  },
  {
    id: 'realistic',
    name: 'Realistic Upgrade',
    emoji: '🧩',
    buttonText: 'Upgrade Image',
    description: 'Subtle improvements while preserving realism',
    subtitle: 'No style shift, no over-processing',
    preview: 'https://r2.zestygen.com/effects/30.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Upgrade this image to photorealistic quality with enhanced realism and natural appearance. Improve skin textures, lighting accuracy, color fidelity, and overall photographic authenticity. Add realistic depth, proper shadows, natural reflections, and lifelike details. Enhance the image to look like a professional high-resolution photograph taken with premium camera equipment, maintaining perfect balance between enhancement and natural realism.',
  },
  {
    id: 'artistic',
    name: 'Artistic Style',
    emoji: '🎨',
    buttonText: 'Apply Artistic Style',
    description: 'Apply artistic flair while keeping the original structure',
    subtitle: 'Great for creative exploration and social sharing',
    preview: 'https://r2.zestygen.com/effects/40.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Transform this image into an artistic masterpiece with creative stylization and artistic interpretation. Apply painterly effects, artistic color palettes, expressive brushstrokes, and creative visual treatments. Blend photographic elements with artistic techniques to create a unique fusion of reality and art. Enhance with artistic lighting, creative composition, and aesthetic refinement while preserving the essence of the original subject.',
  },
  {
    id: 'creative',
    name: 'Creative Reimagine',
    emoji: '🚀',
    buttonText: 'Reimagine Image',
    description: 'Bold transformations with stronger creative interpretation',
    subtitle: 'Best for experimentation and standout visuals',
    preview: 'https://r2.zestygen.com/effects/50.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: 'Creatively reimagine this image with bold artistic vision and innovative visual concepts. Transform the scene with imaginative elements, surreal touches, creative color schemes, and unexpected visual interpretations. Push creative boundaries while maintaining recognizable elements from the original. Apply avant-garde styling, experimental techniques, and visionary aesthetics to create a completely fresh and inspiring interpretation of the image.',
  },
  {
    id: 'custom',
    name: 'Custom',
    emoji: '⚙️',
    buttonText: 'Generate Custom Effect',
    description: 'Create your own unique transformation',
    subtitle: 'Full control with custom prompts',
    preview: 'https://r2.zestygen.com/effects/ai-digital-art.webp',
    model: DEFAULT_MODEL,
    provider: DEFAULT_PROVIDER,
    prompt: '',
    isCustom: true,
  },
];

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function ImageToImageGenerator({
  defaultEffect,
  effects = DEFAULT_IMAGE_TO_IMAGE_EFFECTS,
  allowMultipleImages = false,
  maxImages = 1,
  maxSizeMB = 5,
  srOnlyTitle,
}: ImageToImageGeneratorProps) {
  const t = useTranslations('photo-effects.common.generator');
  const tCheckin = useTranslations('common.checkin-button');

  const [selectedEffect, setSelectedEffect] = useState<string>(
    defaultEffect || effects[0]?.id || ''
  );
  const [costCredits, setCostCredits] = useState<number>(5);
  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);
  const [showAllEffectsModal, setShowAllEffectsModal] = useState(false);
  const [customPrompt, setCustomPrompt] = useState<string>(''); // 自定义 prompt 状态

  // Checkin states
  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [isCheckinLoading, setIsCheckinLoading] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState<CheckinStatus | null>(null);
  const [mounted, setMounted] = useState(false);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  // Fix hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Track page view on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackImageToImagePageView({
        default_effect: defaultEffect,
        page_path: window.location.pathname,
      });
    }
  }, [defaultEffect]);

  // Reorder effects to put defaultEffect first if it exists
  const orderedEffects = useMemo(() => {
    if (!defaultEffect) return effects;

    const defaultIndex = effects.findIndex((e) => e.id === defaultEffect);
    if (defaultIndex === -1) return effects;

    // Move the default effect to the front
    const reordered = [...effects];
    const [defaultEffectItem] = reordered.splice(defaultIndex, 1);
    return [defaultEffectItem, ...reordered];
  }, [defaultEffect, effects]);

  const currentEffect = orderedEffects.find((e) => e.id === selectedEffect);
  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleGenerate = async () => {
    if (referenceImageUrls.length === 0) {
      toast.error(t('errors.reference-image-required'));
      return;
    }

    if (!currentEffect) {
      toast.error(t('errors.effect-required'));
      return;
    }

    // 如果是自定义效果，检查 prompt 是否为空
    if (currentEffect.isCustom && !customPrompt.trim()) {
      toast.error('Please enter a custom prompt');
      return;
    }

    if (isCheckSign) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < costCredits) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    // Track generate button click
    trackGenerateClick({
      effect_id: selectedEffect,
      effect_name: currentEffect.name,
      is_custom: currentEffect.isCustom || false,
      has_custom_prompt: !!(currentEffect.isCustom && customPrompt.trim().length > 0),
      credits_cost: costCredits,
      remaining_credits: remainingCredits,
    });

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());

    try {
      // Use image-to-image scene with reference images
      const options: any = {
        image_input: referenceImageUrls,
      };

      // 使用自定义 prompt 或预设 prompt
      const finalPrompt = currentEffect.isCustom ? customPrompt : currentEffect.prompt;

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'photo-effects', // Special scene for photo effects (1 credit)
          provider: currentEffect.provider,
          model: currentEffect.model,
          prompt: finalPrompt,
          options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('errors.generation-failed'));
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || t('errors.generation-failed'));
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          const timestamp = Date.now();
          const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
            id: `${newTaskId}-${timestamp}-${index}`,
            url,
            effect: selectedEffect,
            prompt: currentEffect.prompt || undefined,
          }));

          setGeneratedImages((prev) => [...newImages, ...prev]);
          toast.success(t('success.image-generated'));

          // Track photo effects for analytics
          trackPhotoEffects({
            effect: selectedEffect,
            credits_used: costCredits,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Image generation error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const pollTask = async (): Promise<boolean> => {
      try {
        // Check for timeout
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error(t('errors.no-image-url'));
          } else {
            const timestamp = Date.now();
            const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
              id: `${taskId}-${timestamp}-${index}`,
              url,
              effect: selectedEffect,
              prompt: task.prompt || undefined,
            }));

            setGeneratedImages((prev) => [...newImages, ...prev]);
            toast.success(t('success.image-generated'));

            // Track photo effects for analytics
            trackPhotoEffects({
              effect: selectedEffect,
              credits_used: costCredits,
            });
          }

          setProgress(100);
          setIsGenerating(false);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    // Initial poll
    pollTask();

    // Set up interval
    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTask();
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, generationStartTime, selectedEffect, t, fetchUserCredits]);

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      // Fetch image via proxy to avoid CORS issues
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-${image.effect}-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.image-downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingImageId(null);
    }
  };

  const handleSelectEffect = (effectId: string) => {
    const effect = orderedEffects.find((e) => e.id === effectId);
    if (effect) {
      // Track effect selection
      trackEffectSelection({
        effect_id: effectId,
        effect_name: effect.name,
        is_custom: effect.isCustom || false,
      });
    }
    setSelectedEffect(effectId);
    setShowAllEffectsModal(false);
  };

  // Handle opening checkin dialog
  const handleOpenCheckinDialog = async () => {
    // Track checkin dialog open
    trackCheckinDialogOpen({
      source: 'image_to_image',
    });

    if (!user) {
      // 未登录，显示登录弹窗
      setIsShowSignModal(true);
      return;
    }

    setIsCheckinOpen(true);

    // 获取签到状态
    try {
      const response = await fetch('/api/checkin/claim?action=status');
      if (response.ok) {
        const status = await response.json();
        setCheckinStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch checkin status:', error);
    }
  };

  // Handle checkin
  const handleCheckin = async () => {
    if (!user) {
      setIsCheckinOpen(false);
      return;
    }

    setIsCheckinLoading(true);
    try {
      const response = await fetch('/api/checkin/claim', {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();

        // 埋点：签到失败
        trackDailyCheckin({
          credits_earned: 0,
          consecutive_days: checkinStatus?.consecutiveDays || 0,
          success: false,
          error_message: error.error || 'Checkin failed',
        });

        throw new Error(error.error || 'Checkin failed');
      }

      const result = await response.json();
      const dayKey = Math.min(result.consecutiveDays, 5);

      // 埋点：签到成功
      trackDailyCheckin({
        credits_earned: result.rewardCredits,
        consecutive_days: result.consecutiveDays,
        success: true,
      });

      // 使用新的成功文案
      toast.success(`Day ${dayKey} Check-in Success!`, {
        description: `You earned ${result.rewardCredits} credits!`,
        duration: 3000,
      });

      // 更新状态
      setCheckinStatus({
        hasCheckedInToday: true,
        consecutiveDays: result.consecutiveDays,
        tomorrowRewardCredits: Math.min(result.consecutiveDays + 1, 5),
      });

      // 刷新用户积分
      await fetchUserCredits();

      // 延迟关闭对话框
      setTimeout(() => {
        setIsCheckinOpen(false);
      }, 2000);
    } catch (error: any) {
      toast.error('Checkin failed', {
        description: error.message,
      });
    } finally {
      setIsCheckinLoading(false);
    }
  };

  return (
    <section className="py-4 md:py-6">
      <div className="container">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column - Upload & Generate */}
            <Card>
              <CardHeader className="pb-4">
                {srOnlyTitle && <h2 className="sr-only">{srOnlyTitle}</h2>}
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  {t('title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pb-4">
                <div className="space-y-2">
                  <Label>{t('labels.reference-image')}</Label>
                  <ImageUploader
                    defaultPreviews={referenceImageUrls}
                    onChange={(items) => {
                      setReferenceImageItems(items);
                      const urls = items
                        .filter((item) => item.status === 'uploaded' && item.url)
                        .map((item) => item.url!);
                      setReferenceImageUrls(urls);

                      // Track image upload when successfully uploaded
                      const uploadedItems = items.filter((item) => item.status === 'uploaded');
                      if (uploadedItems.length > 0) {
                        // Calculate total size in MB
                        const totalSizeMB = uploadedItems.reduce((sum, item) => {
                          return sum + (item.size || 0);
                        }, 0) / (1024 * 1024);

                        trackImageUpload({
                          image_count: uploadedItems.length,
                          total_size_mb: parseFloat(totalSizeMB.toFixed(2)),
                        });
                      }
                    }}
                    allowMultiple={allowMultipleImages}
                    maxImages={maxImages}
                    maxSizeMB={maxSizeMB}
                  />
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">
                      {t('labels.image-format')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('labels.image-privacy')}
                    </p>
                  </div>
                </div>

                {/* Effect Selector - Integrated */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">
                    {t('effects.title')}
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {orderedEffects.map((effect) => {
                      const isSelected = selectedEffect === effect.id;
                      return (
                        <button
                          key={effect.id}
                          onClick={() => handleSelectEffect(effect.id)}
                          className={cn(
                            'group relative overflow-hidden rounded-md transition-all duration-200',
                            isSelected
                              ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                              : 'hover:ring-2 hover:ring-muted-foreground/30 hover:ring-offset-1 hover:ring-offset-background'
                          )}
                        >
                          <div className="relative w-full h-20 overflow-hidden rounded-t-md">
                            <img
                              src={effect.preview}
                              alt={effect.name}
                              className={cn(
                                'h-full w-full object-cover transition-transform duration-300',
                                isSelected ? 'scale-105' : 'group-hover:scale-105'
                              )}
                            />
                            {isSelected && (
                              <div className="absolute inset-0 bg-primary/20" />
                            )}
                            {isSelected && (
                              <div className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                <Check className="h-2 w-2" />
                              </div>
                            )}
                          </div>
                          <div
                            className={cn(
                              'flex items-center justify-center gap-0.5 px-1 py-1 text-center text-xs font-medium transition-colors',
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted/50 group-hover:bg-muted'
                            )}
                          >
                            <span className="text-xs">{effect.emoji}</span>
                            <span className="text-[9px] leading-tight truncate">{effect.name}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Effect Description - Show for selected effect */}
                  {currentEffect && (
                    <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                      <div className="flex items-start gap-2">
                        <span className="text-lg mt-0.5">{currentEffect.emoji}</span>
                        <div className="flex-1 space-y-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            {currentEffect.name}
                          </h4>
                          <p className="text-xs text-foreground/80 leading-relaxed">
                            {currentEffect.description}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {currentEffect.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Prompt Input - Only show when Custom is selected */}
                {currentEffect?.isCustom && (
                  <div className="space-y-2">
                    <Label htmlFor="custom-prompt" className="text-sm font-medium">
                      Custom Prompt
                    </Label>
                    <Textarea
                      id="custom-prompt"
                      placeholder="Describe how you want to transform your image..."
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      className="min-h-[100px] resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      Describe the style, effects, or transformations you want to apply to your image.
                    </p>
                  </div>
                )}

                {/* Credits Info - Subtle */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span>{costCredits} {t('labels.credits')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{remainingCredits} {t('labels.remaining-credits')}</span>
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    referenceImageUrls.length === 0 ||
                    !currentEffect ||
                    (currentEffect.isCustom && !customPrompt.trim())
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {currentEffect?.buttonText || t('buttons.generate')}
                    </>
                  )}
                </Button>

                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('labels.progress')}
                      </span>
                      <span className="font-medium">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} />
                    {taskStatus && (
                      <p className="text-center text-xs text-muted-foreground">
                        {t(`status.${taskStatus.toLowerCase()}`)}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column - Results */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <ImageIcon className="h-5 w-5" />
                  {t('results.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedImages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="relative min-h-[500px] overflow-hidden rounded-lg border-2 border-dashed flex items-center justify-center">
                      {currentEffect && (
                        <div className="absolute inset-0 flex items-center justify-center p-4">
                          <img
                            src={currentEffect.preview}
                            alt={currentEffect.name}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                    <div className="text-center space-y-2 py-4">
                      <div className="flex justify-center">
                        <ImageIcon className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {t('results.no-images')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('results.preview-hint')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-4">
                        {generatedImages.map((image) => (
                          <div
                            key={image.id}
                            className="group relative overflow-hidden rounded-lg border"
                          >
                            <LazyImage
                              src={image.url}
                              alt={`Generated with ${image.effect}`}
                              className="w-full"
                            />
                            <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleDownloadImage(image)}
                                disabled={downloadingImageId === image.id}
                              >
                                {downloadingImageId === image.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    {/* Checkin Prompt - Shows after image generation */}
                    <div className="mt-6 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-4">
                      <div className="text-center space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          🎉 Want more free credits?
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Check in daily to earn free credits • Unlock unlimited transformations
                        </p>
                        <Button
                          size="sm"
                          variant="default"
                          className="mt-2"
                          onClick={handleOpenCheckinDialog}
                        >
                          Daily Check-in
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkin Dialog */}
      {mounted && (
        <Dialog open={isCheckinOpen} onOpenChange={setIsCheckinOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                <Flame className="inline h-5 w-5 text-orange-500 mr-2" />
                {tCheckin('title')}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {tCheckin('subtitle')}
              </DialogDescription>
            </DialogHeader>

            {checkinStatus ? (
              <div className="space-y-5">
                {/* 今日奖励卡片 - 未签到时显示 */}
                {!checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_title`)}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-primary">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_main`)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_sub`)}
                      </p>
                      <p className="text-xs text-muted-foreground/80">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_desc`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 签到成功卡片 - 已签到时显示 */}
                {checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-green-500/30 bg-gradient-to-br from-green-50 via-green-100/50 to-green-50 dark:from-green-950/20 dark:via-green-900/30 dark:to-green-950/20 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                      <span className="text-2xl">🎉</span>
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_title`)}
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_reward`)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_available`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 明日预告 - 仅在未达到第5天时显示 */}
                {!checkinStatus.hasCheckedInToday && checkinStatus.consecutiveDays < 4 && (
                  <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.tomorrow_preview`)}
                    </h4>
                    <p className="text-sm font-semibold text-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 2, 5)}.reward_main`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 2, 5)}.reward_sub`)}
                    </p>
                  </div>
                )}

                {/* 连续天数统计 */}
                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {checkinStatus.consecutiveDays}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tCheckin('consecutive-days')}
                    </div>
                  </div>
                  {checkinStatus.hasCheckedInToday && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">✓</div>
                      <div className="text-xs text-muted-foreground">
                        {tCheckin('status')}
                      </div>
                    </div>
                  )}
                </div>

                {/* 签到按钮 */}
                <Button
                  onClick={handleCheckin}
                  disabled={checkinStatus.hasCheckedInToday || isCheckinLoading}
                  size="lg"
                  className="w-full text-base font-semibold"
                >
                  {checkinStatus.hasCheckedInToday
                    ? tCheckin('button-checked')
                    : tCheckin(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.cta`)}
                </Button>

                {/* 查看详情链接 */}
                <div className="text-center">
                  <Link
                    href="/settings/checkin"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setIsCheckinOpen(false)}
                  >
                    {tCheckin('view-details')} →
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">
                  {tCheckin('loading')}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
